import {inject, Injectable} from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {MsalService} from '@azure/msal-angular';
import {environment} from "../../../environments/environment";

@Injectable({
    providedIn: 'root'
})
export class AiChatService {
    private authService = inject(MsalService);
    private connection!: signalR.HubConnection;
    private isConnected = false;
    private startPromise?: Promise<void>;

    public onChunk: (chunk: string) => void = () => {
    };
    public onComplete: () => void = () => {
    };
    public onError: (message: string) => void = () => {
    };
    public onAdminChatChunk: (chunk: string) => void = () => {
    };
    public onAdminChatArtifacts: (artifacts: unknown[]) => void = () => {
    };
    public onAdminChatDataChanged: (changes: unknown[]) => void = () => {
    };
    public onAdminChatToolActivities: (activities: unknown[]) => void = () => {
    };
    public onAdminChatComplete: () => void = () => {
    };
    public onAdminChatError: (message: string) => void = () => {
    };

    connect(): Promise<void> {
        if (this.isConnected) return Promise.resolve();
        if (this.startPromise) return this.startPromise;

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`${environment.apiUrl}/ai-hub`, {
                accessTokenFactory: () => this.getAccessToken(),
                withCredentials: true,
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 10000);
                }
            })
            .build();

        this.connection.on('dayTextChunk', chunk => {
            if (this.onChunk) this.onChunk(chunk);
        });

        this.connection.on('dayTextCompleted', () => {
            if (this.onComplete) this.onComplete();
        });

        this.connection.on('aiError', message => {
            if (this.onError) this.onError(message);
        });

        this.connection.on('adminChatChunk', chunk => {
            if (this.onAdminChatChunk) this.onAdminChatChunk(chunk);
        });

        this.connection.on('adminChatArtifacts', artifacts => {
            if (this.onAdminChatArtifacts) this.onAdminChatArtifacts(artifacts ?? []);
        });

        this.connection.on('adminChatDataChanged', changes => {
            if (this.onAdminChatDataChanged) this.onAdminChatDataChanged(changes ?? []);
        });

        this.connection.on('adminChatToolActivities', activities => {
            if (this.onAdminChatToolActivities) this.onAdminChatToolActivities(activities ?? []);
        });

        this.connection.on('adminChatEnd', () => {
            if (this.onAdminChatComplete) this.onAdminChatComplete();
        });

        this.connection.on('adminChatError', message => {
            if (this.onAdminChatError) this.onAdminChatError(message);
        });

        this.startPromise = this.connection
            .start()
            .then(() => {
                this.isConnected = true;
                this.startPromise = undefined;
            })
            .catch(err => {
                this.startPromise = undefined;
                console.error('SignalR connection failed', err);
                throw err;
            });

        return this.startPromise;
    }

    private async getAccessToken(): Promise<string> {
        const account = this.authService.instance.getActiveAccount() || this.authService.instance.getAllAccounts()[0];

        if (!account) {
            return '';
        }

        const result = await this.authService.instance.acquireTokenSilent({
            account,
            scopes: [environment.msal.apiScope],
        });

        return result.accessToken;
    }

    startDayTextGeneration(inquiryId: number, travelDayId: number, userPrompt?: string): void {
        if (!this.connection) {
            this.connect();
            throw new Error('SignalR connection not established.');
        }

        this.connection.invoke('StartDayTextGeneration', inquiryId, travelDayId, userPrompt ?? null)
            .catch(err => console.error('Error invoking generation', err));
    }

    startDayTextDescriptionRegeneration(
        inquiryId: number,
        travelDayId: number,
        userPrompt?: string
    ): void {
        if (!this.connection) {
            this.connect();
            throw new Error('SignalR connection not established.');
        }

        this.connection.invoke(
            'StartDayTextDescriptionRegeneration',
            inquiryId,
            travelDayId,
            userPrompt ?? null
        )
            .catch(err => console.error('Error invoking generation', err));
    }

    async sendAdminChatMessage(request: unknown): Promise<void> {
        await this.connect();

        return this.connection.invoke('SendAdminChatMessage', request)
            .catch(err => {
                console.error('Error invoking admin chat', err);
                throw err;
            });
    }
}
